import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Info, Loader2, X, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * WhyThisTooltip — reveals the derivation trace of a personalised
 * recommendation on demand. Wraps `GET /api/recommendations/{user_id}/explain/{pattern_key}`.
 *
 * Auto-picks the user's top recommendation when no `patternKey` is provided,
 * so the widget can render a single "Why this?" chip without the parent
 * having to pass every rec's pattern key upfront.
 */

const WhyThisTooltip = ({
  userId,
  patternKey: initialPatternKey = null,
  variant = 'chip',         // 'chip' | 'icon'
  label = 'Why this?',
  testid = 'why-this-tooltip',
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      let pk = initialPatternKey;
      // When no pattern key supplied, pull the top rec and use its key.
      if (!pk) {
        const list = await axios.get(
          `${BACKEND_URL}/api/recommendations/${userId}?limit=1`,
          { withCredentials: true },
        );
        pk = list.data?.recommendations?.[0]?.pattern_key;
        if (!pk) {
          setError('No recommendations yet — scan a few products first.');
          setLoading(false);
          return;
        }
      }
      const r = await axios.get(
        `${BACKEND_URL}/api/recommendations/${userId}/explain/${encodeURIComponent(pk)}`,
        { withCredentials: true },
      );
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load explanation.');
    }
    setLoading(false);
  }, [userId, initialPatternKey]);

  const openModal = () => {
    setOpen(true);
    if (!data) load();
  };

  const closeModal = () => setOpen(false);

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={openModal}
          aria-label={label}
          title={label}
          className="inline-flex items-center justify-center rounded-full p-1 text-indigo-600 hover:bg-indigo-50 transition"
          data-testid={testid}
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          type="button"
          onClick={openModal}
          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full bg-white/90 border border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition"
          data-testid={testid}
        >
          <Info className="w-3 h-3" />
          {label}
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeModal}
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
          data-testid={`${testid}-modal`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 text-white px-4 py-3 flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                <div>
                  <div className="text-sm font-bold">Why this recommendation?</div>
                  <div className="text-[11px] opacity-90">Powered by your habit patterns + live prices</div>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="text-white/90 hover:text-white p-1 rounded"
                data-testid={`${testid}-close`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {loading && (
                <div className="flex items-center justify-center gap-2 py-6 text-gray-400 text-sm" data-testid={`${testid}-loading`}>
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading explanation…
                </div>
              )}

              {error && (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`${testid}-error`}>
                  {error}
                </div>
              )}

              {data && (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Product</div>
                    <div className="text-base font-bold text-gray-900" data-testid={`${testid}-product`}>
                      {data.product_name || data.recommendation?.product_name || '—'}
                    </div>
                  </div>

                  {data.derivation_trace?.length > 0 ? (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">The reasoning</div>
                      <ul className="space-y-1.5" data-testid={`${testid}-trace`}>
                        {data.derivation_trace.map((line, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-800">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">No detailed trace available for this recommendation yet.</div>
                  )}

                  {data.seasonal?.window && (
                    <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2" data-testid={`${testid}-seasonal`}>
                      <span className="font-semibold">Seasonal boost:</span> {data.seasonal.window} · ×{Number(data.seasonal.multiplier).toFixed(2)}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="border-t border-gray-100 px-4 py-2.5 flex justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="text-xs font-semibold text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded"
                data-testid={`${testid}-dismiss`}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhyThisTooltip;
